const cache = {}; // temporary cache system for repeated requests

const requestQueue = {};
const cacheTimeout = 1000;

const sortObjectKeys = (obj) => {
	if (typeof obj !== 'object' || obj === null) {
		return obj;
	}

	if (Array.isArray(obj)) {
		return obj.map(sortObjectKeys);
	}

	return Object.keys(obj)
		.sort()
		.reduce((result, key) => {
			result[key] = sortObjectKeys(obj[key]);
			return result;
		}, {});
};

const generateCacheKey = (config) => {
	const { method, url, data } = config;
	let sortedData = data || '{}';

	if (typeof data === 'string') {
		try {
			const parsedData = JSON.parse(data);
			sortedData = JSON.stringify(sortObjectKeys(parsedData));
		} catch (e) {
			sortedData = data;
		}
	} else if (typeof data === 'object') {
		sortedData = JSON.stringify(sortObjectKeys(data));
	}

	return `${method}-${url}-${sortedData}`;
};

const clearCacheAfterDelay = (cacheKey, delay) => {
	setTimeout(() => {
		delete cache[cacheKey];
	}, delay);
};

export default {
	request: {
		name: 'Cache repeated request with same data',
		config: async function (config) {
			if (config?.noCache || config.method === 'put') {
				return config;
			}

			const cacheKey = generateCacheKey(config);

			if (cache[cacheKey]) {
				return Promise.reject({ config, __cachedResponse: cache[cacheKey] });
			}

			if (requestQueue[cacheKey]) {
				return new Promise((resolve, reject) => {
					requestQueue[cacheKey].push({ resolve, reject });
				}).then(() => config);
			}

			requestQueue[cacheKey] = [];
			return config;
		},
		error: function (error) {
			if (error.config?.noCache) {
				return Promise.reject(error);
			}
			const cacheKey = generateCacheKey(error.config);
			requestQueue[cacheKey] = undefined;
			return Promise.reject(error);
		},
	},
	response: {
		name: 'clear cache after 2 seconds of the request is made',
		config: async function (response) {
			if (response.config?.noCache) {
				return response;
			}
			const cacheKey = generateCacheKey(response.config);

			cache[cacheKey] = response;
			clearCacheAfterDelay(cacheKey, 2 * cacheTimeout);

			if (requestQueue[cacheKey]) {
				requestQueue[cacheKey]?.forEach(({ resolve }) => resolve());
			}

			delete requestQueue[cacheKey];

			return response;
		},
		error: function (error) {
			if (error.config?.noCache) {
				return Promise.reject(error);
			}
			const cacheKey = generateCacheKey(error.config);

			if (error.__cachedResponse) {
				return Promise.resolve(error.__cachedResponse);
			}

			requestQueue[cacheKey]?.forEach(({ reject }) => reject(error));
			delete requestQueue[cacheKey];

			return Promise.reject(error);
		},
	},
};
